import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, PanelHeader } from "@/components/Common";
import {
  ShieldCheck, ArrowRightCircle, HelpCircle, AlertTriangle, ListTree, Scale, Info,
} from "lucide-react";

const TONE = {
  addressable: {
    accent: "border-l-teal-500", head: "bg-teal-50/70", Icon: ShieldCheck,
    chip: "border-teal-200 bg-teal-50 text-teal-800",
    tag: "Primary first-pilot opportunity", tagCls: "border-teal-300 bg-teal-100 text-teal-800",
  },
  downstream: {
    accent: "border-l-slate-400", head: "bg-slate-50", Icon: ArrowRightCircle,
    chip: "border-slate-200 bg-slate-100 text-slate-600",
    tag: "Not first-pilot scope", tagCls: "border-slate-300 bg-slate-100 text-slate-600",
  },
  review: {
    accent: "border-l-amber-500", head: "bg-amber-50/70", Icon: HelpCircle,
    chip: "border-amber-200 bg-amber-50 text-amber-800",
    tag: "Case-by-case", tagCls: "border-amber-300 bg-amber-100 text-amber-800",
  },
};

export default function PilotDenialTaxonomy() {
  const [data, setData] = useState(null);
  useEffect(() => { api.denialTaxonomy().then(setData).catch(() => {}); }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading pilot denial taxonomy…</div>;

  return (
    <div>
      <PageHeader testid="denial-taxonomy-header" eyebrow="Pilot Validation"
        title="Pilot Denial Taxonomy"
        subtitle="A structured classification of laboratory denial types, grouped by whether RequiSure could potentially address them before the lab performs a test. Provided by domain collaborators to focus the first pilot — not validated payer rules." />

      {/* Prominent collaborator disclaimer */}
      <div data-testid="taxonomy-disclaimer"
        className="mb-5 flex items-start gap-2.5 rounded-md border-l-4 border-l-amber-500 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <span>{data.disclaimer}</span>
      </div>

      {/* Three pilot classification groups */}
      <div className="mb-6 space-y-3">
        {data.groups.map((g) => {
          const t = TONE[g.tone] || TONE.review;
          const Icon = t.Icon;
          return (
            <Panel key={g.id} className={`border-l-2 ${t.accent}`} data-testid={`taxonomy-group-${g.id}`}>
              <div className={`flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-2.5 ${t.head}`}>
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-900 font-display text-xs font-bold text-white">{g.id}</span>
                  <Icon className="h-4 w-4 text-slate-500" />
                  <span className="font-display text-sm font-bold text-slate-900">{g.name}</span>
                </div>
                <span className={`rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${t.tagCls}`}>{t.tag}</span>
              </div>
              <div className="p-4">
                <p className="mb-3 text-xs text-slate-500">{g.note}</p>
                <div className="flex flex-wrap gap-1.5">
                  {g.items.map((it) => (
                    <span key={it} className={`rounded-md border px-2 py-1 text-[11px] font-medium ${t.chip}`}>{it}</span>
                  ))}
                </div>
              </div>
            </Panel>
          );
        })}
      </div>

      {/* ≈50% collaborator estimate — secondary context, never a hero KPI */}
      <Panel className="mb-6" data-testid="taxonomy-estimate">
        <PanelHeader label="Collaborator Hypothesis" icon={Info} />
        <div className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-display text-lg font-bold text-slate-600">{data.estimate.value_label}</span>
            <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
              Collaborator Estimate — Requires Validation
            </span>
          </div>
          <p className="mt-2 max-w-3xl text-xs text-slate-600">{data.estimate.statement}</p>
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">This figure is explicitly NOT</div>
            <div className="flex flex-wrap gap-1.5">
              {data.estimate.not_a.map((n) => (
                <span key={n} className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{n}</span>
              ))}
            </div>
          </div>
        </div>
      </Panel>

      {/* Future preventability classification (never assigned autonomously) */}
      <Panel className="mb-6" data-testid="taxonomy-preventability">
        <PanelHeader label="Denial Preventability Classification (future, human-assigned)" icon={Scale} />
        <div className="grid gap-3 p-4 md:grid-cols-3">
          {data.preventability.map((p) => (
            <div key={p.key} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`taxonomy-prev-${p.key}`}>
              <div className="text-sm font-semibold text-slate-800">{p.label}</div>
              <div className="mt-1 text-xs text-slate-500">{p.desc}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Raw collaborator categories → pilot grouping */}
      <Panel data-testid="taxonomy-raw">
        <PanelHeader label="Collaborator-Supplied Denial Categories" icon={ListTree}
          right={<span className="text-[11px] text-slate-400">{data.categories_raw.length} categories</span>} />
        <div className="divide-y divide-slate-100">
          {data.categories_raw.map((c, i) => (
            <div key={c.category} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start" data-testid={`taxonomy-raw-${i}`}>
              <div className="w-full shrink-0 text-sm font-semibold text-slate-800 sm:w-64">{c.category}</div>
              <div className="flex flex-wrap gap-1.5">
                {c.examples.map((e) => (
                  <span key={e} className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600">{e}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
